import { cn } from '@/lib/utils';
import { SquareSlash } from 'lucide-react';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandGroup,
    CommandItemAnimated,
    CommandListScrollable
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { debugModeState } from '@/state/chat';

import { AgentDebugView, LOCAL_COMMANDS } from './localCommands';

interface Props {
    disabled?: boolean;
    onSelect: (view: AgentDebugView) => void;
}

/**
 * Composer launcher for local "slash commands", shown ONLY when Debug mode is
 * enabled (admin settings → Debug mode). It mirrors the Claude Code composer's
 * slash-command button — a square-slash icon — and is styled as a peer of the
 * adjacent "+" more-options button. Pressing it surfaces `/agent_debug`, which
 * opens the Agent debug modal. `/agent_debug` can still be typed directly in any
 * mode. When Debug mode is off, the button renders nothing.
 */
export const AgentDebugCommandButton = ({
    disabled = false,
    onSelect
}: Props) => {
    const debugMode = useRecoilValue(debugModeState);
    const [open, setOpen] = useState(false);

    if (!debugMode) return null;

    const handleSelect = (view: AgentDebugView) => {
        setOpen(false);
        onSelect(view);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <Button
                                id="agent-debug-command-button"
                                variant="tertiary"
                                size="icon"
                                aria-haspopup="menu"
                                aria-expanded={open}
                                aria-controls="agent-debug-command-popover"
                                className="shrink-0"
                                disabled={disabled}
                            >
                                <SquareSlash />
                            </Button>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Debug commands</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <PopoverContent
                id="agent-debug-command-popover"
                align="start"
                sideOffset={12}
                data-popover-content
                className={cn(
                    'bg-background shadow-md p-1.5 border rounded-lg w-[300px] text-popover-foreground',
                    'animate-in fade-in-0 zoom-in-95 duration-200',
                    'focus:outline-hidden'
                )}
            >
                <div className="px-2 pt-1.5 pb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider select-none">
                    Debug commands
                </div>
                <Command className="bg-transparent overflow-hidden">
                    <CommandListScrollable
                        maxItems={5}
                        className="custom-scrollbar"
                    >
                        <CommandGroup className="p-0">
                            {LOCAL_COMMANDS.map((command, index) => (
                                <CommandItemAnimated
                                    key={command.id}
                                    index={index}
                                    isSelected={index === 0}
                                    onSelect={() => handleSelect(command.view)}
                                    className="items-start gap-3 data-[selected=true]:bg-accent px-2 py-2.5 rounded-md data-[selected=true]:text-accent-foreground"
                                >
                                    <div className="flex justify-center items-center bg-background border border-border rounded-md size-8 text-foreground shrink-0">
                                        <SquareSlash className="size-4!" />
                                    </div>
                                    <div className="flex-1 space-y-0.5 min-w-0">
                                        <div className="font-mono font-medium text-foreground text-sm">
                                            {command.id}
                                        </div>
                                        <div className="text-muted-foreground text-xs leading-snug">
                                            {command.description}
                                        </div>
                                    </div>
                                </CommandItemAnimated>
                            ))}
                        </CommandGroup>
                    </CommandListScrollable>
                </Command>
                <div className="flex items-center gap-1.5 mt-1.5 px-2 pt-2 pb-1 border-border border-t text-[11px] text-muted-foreground select-none">
                    <span>Type</span>
                    <kbd className="inline-flex justify-center items-center bg-muted px-1 border border-border rounded min-w-4 h-4 font-mono text-[10px] text-foreground leading-none">
                        /
                    </kbd>
                    <span>anytime</span>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default AgentDebugCommandButton;
